let browserClient = null;
let browserClientPromise = null;
let uiBundlePromise = null;
const mountedClerkUiNodes = new WeakMap();

function readPublicEnv(name) {
    const value = import.meta.env?.[name];
    return typeof value === "string" ? value.trim() : "";
}

function getPublishableKey(override = "") {
    const value = typeof override === "string" ? override.trim() : "";
    return value || readPublicEnv("VITE_CLERK_PUBLISHABLE_KEY");
}

function decodeBase64(value = "") {
    if (typeof atob === "function") return atob(value);
    if (typeof Buffer !== "undefined") return Buffer.from(value, "base64").toString("utf8");
    throw new Error("Base64 decoder is unavailable");
}

function deriveClerkDomain(publishableKey) {
    const encoded = String(publishableKey || "").split("_")[2] || "";
    const decoded = decodeBase64(encoded);
    return decoded.endsWith("$") ? decoded.slice(0, -1) : decoded;
}

async function loadClerkUiBundle({
    publishableKey,
    documentRef = document,
    windowRef = window
} = {}) {
    if (windowRef?.__internal_ClerkUICtor) return true;
    if (uiBundlePromise) return uiBundlePromise;

    const clerkDomain = deriveClerkDomain(publishableKey);
    if (!clerkDomain) {
        throw new Error("Failed to derive Clerk domain from VITE_CLERK_PUBLISHABLE_KEY");
    }

    uiBundlePromise = new Promise((resolve, reject) => {
        const script = documentRef.createElement("script");
        script.src = `https://${clerkDomain}/npm/@clerk/ui@1/dist/ui.browser.js`;
        script.async = true;
        script.crossOrigin = "anonymous";
        script.onload = () => resolve(true);
        script.onerror = () => reject(new Error("Failed to load Clerk UI bundle"));
        documentRef.head.appendChild(script);
    }).catch((error) => {
        uiBundlePromise = null;
        throw error;
    });

    return uiBundlePromise;
}

async function createClerkBrowserClient({
    importClerkModule = () => import("@clerk/clerk-js"),
    publishableKey = "",
    loadUiBundle = loadClerkUiBundle,
    documentRef = document,
    windowRef = window
} = {}) {
    const resolvedKey = getPublishableKey(publishableKey);
    if (!resolvedKey) {
        throw new Error(
            "Missing VITE_CLERK_PUBLISHABLE_KEY. Set it in your Vite environment."
        );
    }

    const [{ Clerk }] = await Promise.all([
        importClerkModule(),
        loadUiBundle({
            publishableKey: resolvedKey,
            documentRef,
            windowRef
        })
    ]);

    const clerk = new Clerk(resolvedKey);
    await clerk.load({
        ui: { ClerkUI: windowRef.__internal_ClerkUICtor }
    });
    return clerk;
}

function resolveUserEmail(user) {
    if (!user) return "";
    const primary = user.primaryEmailAddress?.emailAddress;
    if (primary) return primary;
    const fallback = Array.isArray(user.emailAddresses)
        ? user.emailAddresses.find((entry) => entry?.id === user.primaryEmailAddressId)?.emailAddress
        : "";
    return fallback || "";
}

function resolveDisplayName(user) {
    if (!user) return "";
    const fullName = typeof user.fullName === "string" ? user.fullName.trim() : "";
    if (fullName) return fullName;
    const joined = [user.firstName, user.lastName]
        .map((value) => (typeof value === "string" ? value.trim() : ""))
        .filter(Boolean)
        .join(" ");
    if (joined) return joined;
    const username = typeof user.username === "string" ? user.username.trim() : "";
    return username || resolveUserEmail(user) || String(user.id || "");
}

function normalizeSession(client) {
    if (!client?.isSignedIn || !client.user) return null;
    const organization = client.organization
        ? {
            id: client.organization.id,
            name: client.organization.name || "",
            slug: client.organization.slug || ""
        }
        : null;
    const orgRole =
        typeof client.organizationMembership?.role === "string"
            ? client.organizationMembership.role
            : null;

    return {
        sessionId: client.session?.id ?? null,
        user: {
            id: client.user.id,
            email: resolveUserEmail(client.user),
            displayName: resolveDisplayName(client.user),
            imageUrl: client.user.imageUrl || ""
        },
        organization,
        orgRole
    };
}

export async function getClerkBrowserClient(options = {}) {
    const hasCustomFactory =
        options.importClerkModule
        || options.publishableKey
        || options.loadUiBundle
        || options.documentRef
        || options.windowRef;

    if (hasCustomFactory) {
        return createClerkBrowserClient(options);
    }

    if (browserClient) return browserClient;
    if (!browserClientPromise) {
        browserClientPromise = createClerkBrowserClient()
            .then((client) => {
                browserClient = client;
                return client;
            })
            .catch((error) => {
                browserClientPromise = null;
                throw error;
            });
    }
    return browserClientPromise;
}

export async function initializeClerk(options = {}) {
    return getClerkBrowserClient(options);
}

export async function safeClerkBrowserClient({
    getClient = getClerkBrowserClient
} = {}) {
    try {
        return await getClient();
    } catch {
        return null;
    }
}

export async function getClerkAccessToken({
    getClient = getClerkBrowserClient
} = {}) {
    const client = await safeClerkBrowserClient({ getClient });
    if (!client?.session?.getToken) return null;
    try {
        return await client.session.getToken();
    } catch {
        return null;
    }
}

export async function getCurrentSession({
    getClient = getClerkBrowserClient
} = {}) {
    const client = await safeClerkBrowserClient({ getClient });
    return normalizeSession(client);
}

export async function getCurrentUser({
    getClient = getClerkBrowserClient
} = {}) {
    const session = await getCurrentSession({ getClient });
    return session?.user || null;
}

export async function getCurrentOrganization({
    getClient = getClerkBrowserClient
} = {}) {
    const session = await getCurrentSession({ getClient });
    return session?.organization || null;
}

function normalizeAuthRedirectUrl(locationHref = "") {
    const fallbackOrigin =
        typeof window !== "undefined" && window.location?.origin
            ? window.location.origin
            : "";
    const fallbackUrl = fallbackOrigin ? `${fallbackOrigin}/` : "/";
    if (!locationHref) return fallbackUrl;

    try {
        const resolved = fallbackOrigin
            ? new URL(locationHref, fallbackOrigin)
            : new URL(locationHref);
        const pathname = resolved.pathname || "/";
        return `${resolved.origin}${pathname}`;
    } catch {
        return fallbackUrl;
    }
}

export async function openClerkSignIn({
    getClient = getClerkBrowserClient,
    locationHref = typeof window !== "undefined" ? window.location.href : ""
} = {}) {
    const client = await safeClerkBrowserClient({ getClient });
    if (!client?.openSignIn) {
        throw new Error("Clerk is not configured (VITE_CLERK_PUBLISHABLE_KEY)");
    }
    const redirectUrl = normalizeAuthRedirectUrl(locationHref);
    client.openSignIn({
        signInFallbackRedirectUrl: redirectUrl,
        signUpFallbackRedirectUrl: redirectUrl,
        afterSignInUrl: redirectUrl,
        afterSignUpUrl: redirectUrl
    });
    return true;
}

export async function signOutCurrentSession({
    getClient = getClerkBrowserClient
} = {}) {
    const client = await safeClerkBrowserClient({ getClient });
    if (!client?.signOut) return false;
    await client.signOut();
    return true;
}

export async function openClerkOrganizationProfile({
    getClient = getClerkBrowserClient
} = {}) {
    const client = await safeClerkBrowserClient({ getClient });
    if (!client?.openOrganizationProfile) return false;
    client.openOrganizationProfile();
    return true;
}

function clearNode(node) {
    if (!node) return;
    node.replaceChildren();
}

function isClerkUiMounted(node) {
    return node ? mountedClerkUiNodes.get(node) === true : false;
}

function setClerkUiMounted(node, mounted) {
    if (!node) return;
    if (mounted) {
        mountedClerkUiNodes.set(node, true);
        return;
    }
    mountedClerkUiNodes.delete(node);
}

function clearMountedClerkUiNode(node, unmount) {
    if (!node) return;
    if (isClerkUiMounted(node) && typeof unmount === "function") {
        try {
            unmount(node);
        } catch {}
    }
    clearNode(node);
    setClerkUiMounted(node, false);
}

function ensureMountedClerkUiNode(node, mount) {
    if (!node || typeof mount !== "function") return false;
    if (isClerkUiMounted(node) && node.childElementCount > 0) {
        return true;
    }
    clearNode(node);
    mount(node);
    setClerkUiMounted(node, true);
    return true;
}

const ORGANIZATION_SWITCHER_PROPS = Object.freeze({
    hidePersonal: false
});

export async function syncClerkAccountUi({
    getClient = getClerkBrowserClient,
    userButtonNode = null,
    organizationSwitcherNode = null
} = {}) {
    const client = await safeClerkBrowserClient({ getClient });
    if (!client) {
        clearMountedClerkUiNode(userButtonNode, null);
        clearMountedClerkUiNode(organizationSwitcherNode, null);
        return false;
    }

    if (!client.isSignedIn) {
        clearMountedClerkUiNode(userButtonNode, client.unmountUserButton);
        clearMountedClerkUiNode(organizationSwitcherNode, client.unmountOrganizationSwitcher);
        return true;
    }

    ensureMountedClerkUiNode(userButtonNode, client.mountUserButton);
    ensureMountedClerkUiNode(
        organizationSwitcherNode,
        (node) => client.mountOrganizationSwitcher(node, ORGANIZATION_SWITCHER_PROPS)
    );
    return true;
}

export async function subscribeClerkAuthChange({
    getClient = getClerkBrowserClient,
    listener = () => {}
} = {}) {
    const client = await safeClerkBrowserClient({ getClient });
    if (!client?.addListener) return () => {};
    return client.addListener(() => {
        listener(normalizeSession(client));
    });
}
